import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef, useState } from 'react';
import Bookmark from 'lucide-react/dist/esm/icons/bookmark.mjs';
import { skillErrorText, skillLimitText } from './useGyroSkills.js';
/** The name rule gyro-svc enforces (userSkills.ts): 1 to 64 letters, digits, spaces, dots, underscores or hyphens. */
const NAME_RE = /^[A-Za-z0-9 ._-]{1,64}$/;
/**
 * Whether "Save as skill" is offered at all: the service reports personal skills on AND verified this identity on the
 * list read. Without the status field nothing renders; on a host that proves only its app key (verified:false), or
 * before the list answers, the controls stay hidden and the person can still insert "run <name>" (Primary's default 1).
 */
export function saveSkillOffered(status, skills) {
    return status?.personal === true && skills.verified === true;
}
/** Comma-separated aliases as typed, cleaned the way the service does (lower case, whitespace collapsed, no blanks). */
export function parseAliasInput(text) {
    return [...new Set(text.split(',').map((alias) => alias.replace(/\s+/g, ' ').trim().toLowerCase()).filter(Boolean))];
}
/**
 * The inline "Save as skill" box under an answer (and above the composer): Name, a one-line description, aliases
 * (comma separated) and the steps, prefilled with the answer. Save = PUT /v1/skills/new; a 413 names the field over its
 * limit as the box labels it ("Steps", never "body"); success says how to run it. The caller keeps the box mounted on its
 * own open state, so a refusal that turns saving off (a 403 custody refusal) stays on screen with its reason.
 */
export function SaveSkillBox({ skills, body, onClose, onSaved }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [aliases, setAliases] = useState('');
    const [steps, setSteps] = useState(body);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [savedName, setSavedName] = useState(null);
    const nameRef = useRef(null);
    const liveRef = useRef(true);
    useEffect(() => { liveRef.current = true; nameRef.current?.focus(); return () => { liveRef.current = false; }; }, []);
    const save = async () => {
        if (busy)
            return;
        const cleanName = name.replace(/\s+/g, ' ').trim();
        if (!NAME_RE.test(cleanName)) {
            setError('Name: 1 to 64 letters, digits, spaces, dots, underscores or hyphens.');
            return;
        }
        if (!steps.trim()) {
            setError('Add the steps first.');
            return;
        }
        if (steps.trim().length > skills.limits.body) {
            setError(skillLimitText('body', skills.limits.body, ''));
            return;
        }
        setBusy(true);
        setError('');
        try {
            const saved = await skills.save('new', { name: cleanName, description: description.trim(), aliases: parseAliasInput(aliases), body: steps });
            if (!liveRef.current)
                return;
            setSavedName(saved.name);
            onSaved?.(saved.name);
        }
        catch (failure) {
            if (liveRef.current)
                setError(skillErrorText(failure, 'save'));
        }
        finally {
            if (liveRef.current)
                setBusy(false);
        }
    };
    if (savedName !== null) {
        return _jsxs("div", { className: "gyro-share-box gyro-skill-box", role: "group", "aria-label": "Save as skill", children: [_jsx("p", { className: "gyro-share-text", role: "status", children: `Saved. Say "run ${savedName}" in any Gyro chat.` }), _jsx("div", { className: "gyro-action-btns", children: _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: onClose, children: "Done" }) })] });
    }
    return _jsxs("div", { className: "gyro-share-box gyro-skill-box", role: "group", "aria-label": "Save as skill", children: [_jsx("p", { className: "gyro-share-text", children: "Save these steps as a skill. Skills you saved are available in every Gyro chat." }), _jsxs("label", { className: "gyro-skill-field", children: [_jsx("span", { children: "Name" }), _jsx("input", { ref: nameRef, className: "gyro-textinput", value: name, maxLength: 64, placeholder: "Weekly digest", onChange: (event) => setName(event.target.value), disabled: busy, "aria-label": "Skill name" })] }), _jsxs("label", { className: "gyro-skill-field", children: [_jsx("span", { children: "Description" }), _jsx("input", { className: "gyro-textinput", value: description, maxLength: 300, placeholder: "One line on what it does", onChange: (event) => setDescription(event.target.value), disabled: busy, "aria-label": "Skill description" })] }), _jsxs("label", { className: "gyro-skill-field", children: [_jsx("span", { children: "Aliases" }), _jsx("input", { className: "gyro-textinput", value: aliases, placeholder: "Other names, comma separated", onChange: (event) => setAliases(event.target.value), disabled: busy, "aria-label": "Skill aliases" })] }), _jsxs("label", { className: "gyro-skill-field", children: [_jsx("span", { children: "Steps" }), _jsx("textarea", { className: "gyro-textinput gyro-skill-steps", value: steps, rows: 6, onChange: (event) => setSteps(event.target.value), disabled: busy, "aria-label": "Skill steps" })] }), error && _jsx("p", { className: "gyro-warn", role: "alert", children: error }), _jsxs("div", { className: "gyro-action-btns", children: [_jsx("button", { type: "button", className: "gyro-btn", onClick: () => void save(), disabled: busy, children: busy ? 'Saving…' : 'Save' }), _jsx("button", { type: "button", className: "gyro-btn ghost", onClick: onClose, disabled: busy, children: "Cancel" })] })] });
}
/** The action-bar link that opens the box (rendered only when saveSkillOffered says so). */
export function SaveSkillLink({ active, onClick }) {
    return _jsxs("button", { type: "button", className: `gyro-link${active ? ' active' : ''}`, onClick: onClick, title: "Save this answer as a skill you can run by name", "aria-expanded": active, children: [_jsx(Bookmark, { size: 11 }), " Save as skill"] });
}
//# sourceMappingURL=SaveSkill.js.map